import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import SubtaskForm from '../components/SubtaskForm';
import {api} from '../../api';

const PRIORITY_COLORS = { HIGH: 'bg-red-100 text-red-800', MEDIUM: 'bg-yellow-100 text-yellow-800', LOW: 'bg-green-100 text-green-800' };
const STATUS_COLORS = { TODO: 'bg-gray-100 text-gray-800', IN_PROGRESS: 'bg-blue-100 text-blue-800', DONE: 'bg-green-100 text-green-800' };

// Local YYYY-MM-DD, comparable as a string with a subtask's dueDate.
const today = () => new Date().toLocaleDateString('en-CA');

export default function CrmTaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);

  const loadTask = () => api(`/crm-tasks/${id}`).then(r => r.json()).then(setTask);

  useEffect(() => { loadTask(); }, [id]);

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    const body = {
      title: task.title, description: task.description, dueDate: task.dueDate,
      status: task.status, priority: task.priority, dealId: task.dealId,
      contactIds: (task.contacts || []).map(c => c.id),
      notes: [...(task.notes || []), { body: newNote.trim() }]
    };
    const res = await api(`/crm-tasks/${id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (res.ok) { setNewNote(''); loadTask(); }
  };

  const setSubtasks = (update) => setTask(t => ({ ...t, subtasks: update(t.subtasks || []) }));

  const handleAddSubtask = async ({ title, contactId, dueDate }) => {
    const res = await api(`/crm-tasks/${id}/subtasks`, { method: 'POST', body: JSON.stringify({ title, contactId, dueDate }) });
    if (!res.ok) return false;
    const created = await res.json();
    setSubtasks(list => [...list, created]);
    return true;
  };

  // The update endpoint replaces every field, so always send the whole subtask.
  const saveSubtask = async (subtask, changes) => {
    const next = { ...subtask, ...changes };
    const body = { title: next.title, completed: !!next.completed, contactId: next.contactId || null, dueDate: next.dueDate || null };
    const res = await api(`/crm-tasks/${id}/subtasks/${subtask.id}`, { method: 'PUT', body: JSON.stringify(body) });
    if (!res.ok) return false;
    const updated = await res.json();
    setSubtasks(list => list.map(s => s.id === updated.id ? updated : s));
    return true;
  };

  const toggleSubtask = (subtask) => saveSubtask(subtask, { completed: !subtask.completed });

  const handleEditSubtask = async (subtask, values) => {
    const ok = await saveSubtask(subtask, values);
    if (ok) setEditingSubtaskId(null);
    return ok;
  };

  const removeSubtask = async (subtask) => {
    const res = await api(`/crm-tasks/${id}/subtasks/${subtask.id}`, { method: 'DELETE' });
    if (res.ok) setSubtasks(list => list.filter(s => s.id !== subtask.id));
  };

  const handleDelete = async () => {
    await api(`/crm-tasks/${id}`, { method: 'DELETE' });
    navigate('/crm/tasks');
  };

  if (!task) return null;

  return (
    <Layout>
      <Link to="/crm/tasks" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Tasks</Link>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{task.title}</h1>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${PRIORITY_COLORS[task.priority] || ''}`}>{task.priority}</span>
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${STATUS_COLORS[task.status] || ''}`}>{task.status.replace(/_/g, ' ')}</span>
              <Link to={`/crm/tasks/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-blue-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Due Date</p>
            <p className="text-gray-900 font-medium">{task.dueDate || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Deal</p>
            <p className="text-gray-900 font-medium">
              {task.dealId ? <Link to={`/crm/deals/${task.dealId}`} className="text-blue-600 hover:text-blue-800">{task.dealName}</Link> : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Contacts</p>
            <p className="text-gray-900 font-medium">
              {task.contacts && task.contacts.length > 0
                ? task.contacts.map((c, i) => (
                    <span key={c.id}>{i > 0 && ', '}<Link to={`/crm/contacts/${c.id}`} className="text-blue-600 hover:text-blue-800">{c.firstName} {c.lastName}</Link></span>
                  ))
                : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Created</p>
            <p className="text-gray-900 font-medium">{new Date(task.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
        {task.description && (
          <div className="px-4 md:px-6 pb-4 md:pb-6">
            <p className="text-sm text-gray-500 mb-1">Description</p>
            <p className="text-gray-900 whitespace-pre-wrap">{task.description}</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Subtasks</h2>
          {task.subtasks && task.subtasks.length > 0 && (
            <span className="text-sm text-gray-500">{task.subtasks.filter(s => s.completed).length} / {task.subtasks.length} done</span>
          )}
        </div>
        <div className="p-4 md:p-6">
          <div className="mb-4">
            <SubtaskForm onSubmit={handleAddSubtask} />
          </div>
          {task.subtasks && task.subtasks.length > 0 ? (
            <ul className="space-y-2">
              {task.subtasks.map((s) => editingSubtaskId === s.id ? (
                <li key={s.id} className="border-b pb-2 last:border-b-0">
                  <SubtaskForm initial={s} submitLabel="Save" onSubmit={(values) => handleEditSubtask(s, values)} onCancel={() => setEditingSubtaskId(null)} />
                </li>
              ) : (
                <li key={s.id} className="flex items-start gap-3 border-b pb-2 last:border-b-0">
                  <input type="checkbox" checked={s.completed} onChange={() => toggleSubtask(s)} className="h-4 w-4 mt-1 text-blue-600 rounded" />
                  <div className="flex-1 min-w-0">
                    <p className={s.completed ? 'line-through text-gray-400' : 'text-gray-900'}>{s.title}</p>
                    {(s.contact || s.dueDate) && (
                      <p className="text-sm text-gray-500">
                        {s.contact && <Link to={`/crm/contacts/${s.contact.id}`} className="text-blue-600 hover:text-blue-800">{s.contact.firstName} {s.contact.lastName}</Link>}
                        {s.contact && s.dueDate && ' · '}
                        {s.dueDate && <span className={!s.completed && s.dueDate < today() ? 'text-red-600 font-medium' : ''}>Due {s.dueDate}</span>}
                      </p>
                    )}
                  </div>
                  <button type="button" onClick={() => setEditingSubtaskId(s.id)} className="text-sm text-gray-500 hover:text-blue-600">Edit</button>
                  <button type="button" onClick={() => removeSubtask(s)} className="text-gray-400 hover:text-red-600">&times;</button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No subtasks yet.</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
        </div>
        <div className="p-4 md:p-6">
          <form onSubmit={handleAddNote} className="flex gap-2 mb-4">
            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." rows={2} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="submit" disabled={!newNote.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 self-end">Add</button>
          </form>
          {task.notes && task.notes.length > 0 ? (
            <div className="space-y-4">
              {task.notes.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((n) => (
                <div key={n.id} className="border-b pb-3 last:border-b-0">
                  <p className="text-gray-900 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No notes yet.</p>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteModal}
        title="Delete Task"
        message="Are you sure you want to delete this task?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal(false)}
      />
    </Layout>
  );
}
